import {
  useCallback,
  useRef,
  PropsWithChildren,
  useEffect,
  useReducer,
  useLayoutEffect,
  useMemo,
} from "react";
import { Popover } from "react-aria-components";
import { MDSIcon, MDSIconKind } from "@/mds";
import { CommonEditorInput } from "@/mds/components/forms/common-editor-input.tsx/common-editor-input";
import {
  InstructionsEditorMentionItem,
  InstructionsEditorState,
} from "./instructions-editor.types";
import { instructionsEditorReducer } from "./instructions-editor.reducer";
import {
  StyledContainer,
  StyledInputContainer,
  StyledInputArea,
  StyledExpand,
  StyledMentionAnchor,
  StyledMentionPopoverContent,
  StyledMentionPopoverHeader,
  StyledMentionPopoverItem,
} from "./instructions-editor.style";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import { JsonObject } from "type-fest";
import { InstructionsEditorMention } from "./instructions-editor.types";

export type InstructionsEditorFetchDataHandler<T extends InstructionsEditorMentionItem> = ({
  query,
  mentionSymbol,
}: {
  query: string;
  mentionSymbol?: string;
}) => Promise<InstructionsEditorDataSource<T>>;

export type InstructionsEditorSubmitHandler = (payload: {
  tiptapJsonContent: JsonObject;
  mdxContent: string;
  htmlContent: string;
  mentions: InstructionsEditorMention[];
}) => void;

interface Props<T extends InstructionsEditorMentionItem> extends PropsWithChildren {
  placeholder?: string;
  isDisabled?: boolean;
  loadingDelayMs?: number;
  autoFocus?: boolean;
  fetchData: InstructionsEditorFetchDataHandler<T>;
  onSubmit?: InstructionsEditorSubmitHandler;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChange?: () => void;
  inputRef?: React.RefObject<MemCommonRichTextInputInstance>;
  editorDimensions?: {
    minHeight?: number;
    maxHeight?: number;
    padding?: { top: number; right: number; bottom: number; left: number };
  };
  setContent?: string;
  onContentModified?: () => void;
  className?: string;
  disableSetContent?: boolean;
}

export type InstructionsEditorDataSource<T extends InstructionsEditorMentionItem> = {
  data: T[];
  error?: string;
  isLoading?: boolean;
};

export const InstructionsEditor = <T extends InstructionsEditorMentionItem>(props: Props<T>) => {
  const {
    fetchData,
    placeholder,
    isDisabled = false,
    loadingDelayMs = 300,
    autoFocus = false,
    onSubmit,
    onInputFocus,
    onInputBlur,
    onInputChange,
    inputRef: externalInputRef,
    editorDimensions,
    setContent,
    onContentModified,
    className,
    disableSetContent = false,
  } = props;

  const [state, dispatch] = useReducer(instructionsEditorReducer<T>, {
    isOpen: false,
    query: "",
    isEmpty: true,
    isLoading: false,
    data: [],
    hasSearched: false,
    keyboardSelectionIndex: -1,
  } as InstructionsEditorState<T>);

  const internalInputRef = useRef<MemCommonRichTextInputInstance>(null);
  const inputRef = externalInputRef || internalInputRef;
  // 0×0 fixed-position element positioned at the mention trigger's clientRect.
  // Serves as the triggerRef for react-aria's Popover.
  const anchorRef = useRef<HTMLDivElement>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Dedupe SetEditorContent against the last value we pushed. The parent
  // round-trips the editor's own htmlContent back through `setContent`, and
  // SetEditorContent calls forgetMentionInteractions() in the shared editor
  // which wipes the mention index mid-session and breaks arrow/Enter on the
  // first mention.
  const lastSetContentRef = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    if (!setContent) return;
    if (lastSetContentRef.current === setContent) return;
    lastSetContentRef.current = setContent;

    // Wait for editor to finish mounting before pushing content.
    const h = setTimeout(() => {
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.SetEditorContent,
        payload: { content: setContent },
      });
    }, 10);

    return () => clearTimeout(h);
  }, [inputRef, setContent]);

  const cleanupLoadingTimeout = () => {
    if (!loadingTimeoutRef.current) return;
    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = null;
  };

  const handleMentionQuery = useCallback(
    async (queryStr: string, clientRect?: DOMRect) => {
      const trimmedQuery = queryStr.trim();
      const mentionSymbol = trimmedQuery.charAt(0);
      const startsWithMentionSymbol = mentionSymbol === "@" || mentionSymbol === "#";

      if (abortControllerRef.current) abortControllerRef.current.abort();
      cleanupLoadingTimeout();

      if (!startsWithMentionSymbol) {
        dispatch({ type: "RESET" });
        return;
      }

      const query = trimmedQuery.slice(1);
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      loadingTimeoutRef.current = setTimeout(() => {
        if (!abortController.signal.aborted) dispatch({ type: "LOADING_STARTED" });
      }, loadingDelayMs);

      try {
        const result = await fetchData({ query, mentionSymbol });
        if (abortController.signal.aborted) return;
        dispatch({ type: "SEARCH_COMPLETED", payload: result.data });
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error("Failed to fetch data:", error);
        dispatch({ type: "SEARCH_FAILED" });
      } finally {
        cleanupLoadingTimeout();
        dispatch({ type: "LOADING_STOPPED" });
      }

      dispatch({ type: "QUERY_CHANGED", payload: query });
      dispatch({ type: "CLIENT_RECT_CHANGED", payload: clientRect });
    },
    [fetchData, loadingDelayMs]
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  const handleIsEmptyChange = useCallback((empty: boolean) => {
    dispatch({ type: "EMPTY_STATE_CHANGED", payload: empty });
  }, []);

  // Mirror state into a ref so the arrow/enter handlers (captured by the
  // TipTap suggestion plugin) always read the latest values instead of stale
  // closures from when the handlers were first created.
  const stateRef = useRef(state);
  stateRef.current = state;

  const selectItem = useCallback(
    async (id: string) => {
      const item = stateRef.current.data.find(d => d.id === id);
      if (!item) return;

      const label = (await item.onClick?.()) ?? item.label;
      const kind =
        item.kind === "create-new-collection"
          ? MemCommonMentionKind.Collection
          : (item.kind ?? MemCommonMentionKind.Note);

      inputRef.current?.dispatchAction?.({
        kind: MemCommonRichTextInputActionKind.CloseMention,
        payload: { id: item.id, kind, label },
      });
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });
      dispatch({ type: "RESET" });
    },
    [inputRef]
  );

  const handleArrowKeyPressed = useCallback((direction: "up" | "down") => {
    const s = stateRef.current;
    if (!s.isOpen || s.data.length === 0) return;

    const lastIndex = s.data.length - 1;
    const next =
      direction === "up"
        ? s.keyboardSelectionIndex <= 0
          ? lastIndex
          : s.keyboardSelectionIndex - 1
        : s.keyboardSelectionIndex >= lastIndex
          ? 0
          : s.keyboardSelectionIndex + 1;

    dispatch({ type: "KEYBOARD_SELECTION_CHANGED", payload: next });
  }, []);

  const handleEnterPressed = useCallback(() => {
    const s = stateRef.current;
    if (!s.isOpen || s.data.length === 0 || s.keyboardSelectionIndex < 0) return;
    const selectedItem = s.data[s.keyboardSelectionIndex];
    if (selectedItem) selectItem(selectedItem.id);
  }, [selectItem]);

  const isQueryEmpty = !state.query.length;

  const handleSubmit = useCallback(
    (payload: {
      mdxContent: string;
      htmlContent: string;
      markdownContent: string;
      tiptapJsonContent: JsonObject;
    }) => {
      const { mdxContent, htmlContent, tiptapJsonContent } = payload;
      // Remember the editor-originated html so the round-trip back through
      // `setContent` doesn't re-dispatch SetEditorContent (see ref above).
      lastSetContentRef.current = htmlContent;

      onSubmit?.({
        htmlContent,
        mdxContent,
        tiptapJsonContent,
        mentions: parseMentionsFromMarkdown(mdxContent),
      });
    },
    [onSubmit]
  );

  const headerLabel = useMemo(() => {
    if (!state.data.length) return null;
    const firstKind = state.data[0].kind;
    if (firstKind === MemCommonMentionKind.Collection || firstKind === "create-new-collection") {
      return `Tag a${isQueryEmpty ? " recent" : ""} collection`;
    }
    if (firstKind === MemCommonMentionKind.Note) {
      return `Mention a${isQueryEmpty ? " recent" : ""} note`;
    }
    return null;
  }, [state.data, isQueryEmpty]);

  // On Escape or outside-press, tell the editor to close its mention session.
  // TipTap will then emit an empty-query MentionUpdateQuery which flows
  // through handleMentionQuery to reset our state, keeping both in sync.
  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.CloseMention,
        payload: null,
      });
    },
    [inputRef]
  );

  return (
    <StyledContainer data-mds-name="instructions-editor" translate="no" className={className}>
      <StyledMentionAnchor
        ref={anchorRef}
        style={
          state.clientRect
            ? { top: state.clientRect.bottom, left: state.clientRect.left }
            : undefined
        }
      />
      {state.isOpen && state.data.length > 0 && (
        <Popover
          triggerRef={anchorRef}
          isOpen={state.isOpen}
          onOpenChange={handlePopoverOpenChange}
          isNonModal
          placement="bottom start"
          offset={8}
        >
          <StyledMentionPopoverContent
            // Keep focus in the editor when clicking a menu item; without this
            // pointerdown would blur the editor, TipTap would close the mention,
            // and the item's onClick would never fire.
            onMouseDown={e => e.preventDefault()}
          >
            {headerLabel && <StyledMentionPopoverHeader>{headerLabel}</StyledMentionPopoverHeader>}
            {state.data.map((item, index) => (
              <StyledMentionPopoverItem
                key={item.id}
                $isSelected={index === state.keyboardSelectionIndex}
                onMouseEnter={() =>
                  dispatch({ type: "KEYBOARD_SELECTION_CHANGED", payload: index })
                }
                onClick={() => selectItem(item.id)}
              >
                <MDSIcon
                  kind={
                    item.kind === "create-new-collection"
                      ? MDSIconKind.Plus
                      : item.kind === MemCommonMentionKind.Collection
                        ? MDSIconKind.Collection
                        : MDSIconKind.Document
                  }
                />
                {item.label}
              </StyledMentionPopoverItem>
            ))}
          </StyledMentionPopoverContent>
        </Popover>
      )}

      <StyledInputContainer
        aria-disabled={isDisabled}
        data-mds-name="instructions-editor-input-container"
      >
        <StyledInputArea>
          <StyledExpand>
            <CommonEditorInput
              dimensions={editorDimensions}
              onMentionQuerySet={handleMentionQuery}
              onIsEmptyChange={handleIsEmptyChange}
              placeholder={placeholder}
              isDisabled={isDisabled}
              inputRef={inputRef}
              onSubmit={handleSubmit}
              onArrowKeyPressed={handleArrowKeyPressed}
              onEnterPressed={handleEnterPressed}
              onInputFocus={onInputFocus}
              onInputBlur={onInputBlur}
              onInputChange={onInputChange}
              onContentModified={onContentModified}
              content={!disableSetContent ? setContent : undefined}
              autoFocus={autoFocus}
            />
          </StyledExpand>
        </StyledInputArea>
      </StyledInputContainer>
    </StyledContainer>
  );
};

const parseMentionsFromMarkdown = (markdownContent: string): InstructionsEditorMention[] => {
  const mentions: InstructionsEditorMention[] = [];
  const mentionRegex = /<a[^>]*data-type="mention-(note|collection)"[^>]*>/g;

  let match;
  while ((match = mentionRegex.exec(markdownContent)) !== null) {
    const anchorTag = match[0];
    const idMatch = anchorTag.match(/data-mention-id="([^"]+)"/);
    const kindMatch = anchorTag.match(/data-mention-kind="([^"]+)"/);
    const labelMatch = anchorTag.match(/data-mention-fallback-label="([^"]+)"/);
    if (!idMatch || !kindMatch || !labelMatch) continue;

    const [id, kind, label] = [idMatch[1], kindMatch[1], labelMatch[1]];
    if (kind === "collection") {
      mentions.push({
        kind: MemCommonMentionKind.Collection,
        collection_id: id,
        collection_title: label,
      });
    } else if (kind === "note") {
      mentions.push({
        kind: MemCommonMentionKind.Note,
        note_id: id,
        note_primary_label: label,
      });
    }
  }

  return mentions;
};
